import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import GameCard from '../components/GameCard';
import { useGames } from '../hooks/useGames';

// ─── Feature cards ────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: '🔒', title: 'Provably Fair', desc: 'Every outcome is verifiable and tamper-proof.' },
  { icon: '⚡', title: 'Instant Play', desc: 'No downloads. Jump straight into the action.' },
  { icon: '📱', title: 'Mobile Ready', desc: 'Seamless experience on any device, any screen.' },
  { icon: '🎯', title: 'High RTP', desc: 'Industry-leading return-to-player rates across all games.' },
];

// ─── Animated counter ─────────────────────────────────────────────────────────
function Counter({ target, suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      obs.disconnect();
      let start = 0;
      const step = target / 40;
      const tick = setInterval(() => {
        start += step;
        if (start >= target) { setVal(target); clearInterval(tick); }
        else setVal(Math.floor(start));
      }, 30);
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target]);

  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

export default function HomePage() {
  const { games, loading, error } = useGames();
  const featured = games.slice(0, 6);

  return (
    <div className="home-root">
      <Nav />

      {/* ── Hero ── */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-orb hero-orb-1" />
          <div className="hero-orb hero-orb-2" />
          <div className="hero-grid" />
        </div>
        <div className="hero-content">
          <div className="hero-badge">Next-Gen iGaming Platform</div>
          <h1 className="hero-title">
            The Future of<br />
            <span className="hero-title-accent">Online Gaming</span>
          </h1>
          <p className="hero-subtitle">
            Provably fair, lightning-fast games engineered for maximum excitement.
            Place your bet, ride the multiplier, and cash out at the perfect moment.
          </p>
          <div className="hero-ctas">
            <Link className="btn-fill" to="/games">Explore Games</Link>
            <button className="btn-ghost" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
              Learn More
            </button>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <strong><Counter target={games.length || 1} />+</strong>
              <span>Live Games</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <strong><Counter target={12400} />+</strong>
              <span>Active Players</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <strong><Counter target={97} suffix="%" /></strong>
              <span>Avg RTP</span>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <strong>24/7</strong>
              <span>Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Featured games ── */}
      <section className="section" id="games">
        <div className="section-inner">
          <div className="section-header">
            <div className="eyebrow">Our Catalog</div>
            <h2 className="section-title">
              Featured Games{!loading && <span className="section-title-count">{games.length}</span>}
            </h2>
            <p className="section-sub">
              More games launching soon. Start with our flagship hit.
            </p>
          </div>

          {loading && (
            <div className="games-loading">
              <div className="games-loading-spinner" />
              <p>Loading games…</p>
            </div>
          )}

          {error && (
            <div className="games-error">
              <p>Could not load games. Please try again later.</p>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="games-grid">
                {featured.map(game => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
              <div className="view-all-wrap">
                <Link className="btn-ghost" to="/games">View All Games →</Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Features ── */}
      <section className="section section-alt" id="features">
        <div className="section-inner">
          <div className="section-header">
            <div className="eyebrow">Why Us</div>
            <h2 className="section-title">Built Different</h2>
            <p className="section-sub">Everything we build is player-first.</p>
          </div>
          <div className="features-grid">
            {FEATURES.map(f => (
              <div key={f.title} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section className="section" id="about">
        <div className="section-inner about-grid">
          <div className="about-text">
            <div className="eyebrow">About</div>
            <h2 className="section-title">Who We Are</h2>
            <p>
              SunSoftGaming is an independent iGaming studio crafting provably-fair,
              skill-influenced games for modern players. We believe in transparency,
              fair odds, and thrilling gameplay — nothing more, nothing less.
            </p>
            <p>
              Every game on this platform uses a verifiable random number generator.
              Your outcome is determined before you even press play, and you can
              always verify it on-chain.
            </p>
          </div>
          <div className="about-badges">
            {['Provably Fair', 'RNG Certified', 'Instant Payouts', 'Mobile First'].map(b => (
              <div key={b} className="about-badge">{b}</div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
